import { useEffect, useRef, type ReactNode } from 'react';
import { useToast } from '../app/providers';
import { IconAlert, IconCheck, IconInfo, IconX } from './Icons';

/**
 * Toast stack. Rendered once at the app root; screens just call useToast().success(...).
 * Every create, update and delete goes through here, which is the requirement.
 */
export function Toaster() {
  const { toasts, dismiss } = useToast();

  return (
    <div className="toaster" role="region" aria-label="Notifications">
      {toasts.map((toast) => (
        <div
          key={toast.id}
          className={`toast toast--${toast.kind}`}
          // Errors are assertive so a screen reader interrupts; the rest are polite.
          role={toast.kind === 'error' ? 'alert' : 'status'}
        >
          <span className="toast__icon" aria-hidden="true">
            {toast.kind === 'success' && <IconCheck />}
            {toast.kind === 'error' && <IconAlert />}
            {toast.kind === 'warning' && <IconAlert />}
            {toast.kind === 'info' && <IconInfo />}
          </span>

          <span className="toast__message">{toast.message}</span>

          <button
            type="button"
            className="toast__close"
            onClick={() => dismiss(toast.id)}
            aria-label="Dismiss"
          >
            <IconX />
          </button>
        </div>
      ))}
    </div>
  );
}

type ConfirmProps = {
  open: boolean;
  title: string;
  message: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Destructive styling: red confirm button, and the cancel button takes initial focus. */
  destructive?: boolean;
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
};

/**
 * Blocking confirmation for anything irreversible.
 *
 * Required on every delete. The old app deleted straight from an anchor click, so a
 * mis-click removed a fee payment with no way back - these are Restrict-deleted rows with
 * financial meaning.
 */
export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  destructive = false,
  busy = false,
  onConfirm,
  onCancel,
}: ConfirmProps) {
  const cancelRef = useRef<HTMLButtonElement>(null);

  // Focus the safe option, not the destructive one, so Enter cannot nuke a record.
  useEffect(() => {
    if (open) cancelRef.current?.focus();
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onCancel();
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onCancel]);

  if (!open) return null;

  return (
    <div className="modal__backdrop" onMouseDown={onCancel}>
      <div
        className="modal"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="confirm-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className={`modal__icon${destructive ? '' : ' modal__icon--neutral'}`} aria-hidden="true">
          {destructive ? <IconAlert /> : <IconInfo />}
        </div>

        <h2 id="confirm-title" className="modal__title">
          {title}
        </h2>

        <div className="modal__body">{message}</div>

        <div className="modal__actions">
          <button type="button" className="btn" ref={cancelRef} onClick={onCancel} disabled={busy}>
            {cancelLabel}
          </button>
          <button
            type="button"
            className={destructive ? 'btn btn--danger' : 'btn btn--primary'}
            onClick={onConfirm}
            disabled={busy}
          >
            {busy ? <><span className="btn__spin" />Working…</> : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
